import { currencyFractionDigits } from '../../shared/utils/commercial-money';
import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { forkJoin } from 'rxjs';

import { MenuItem, MessageService, SelectItem } from 'primeng/api';
import { BadgeModule } from 'primeng/badge';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { MultiSelectModule } from 'primeng/multiselect';
import { ProgressBarModule } from 'primeng/progressbar';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { ToastModule } from 'primeng/toast';
import { ToolbarModule } from 'primeng/toolbar';
import { TooltipModule } from 'primeng/tooltip';

import { Customer } from '../../customer/customer.model';
import { CustomerService } from '../../customer/customer.service';
import { SessionStorageService } from '../../services/session-storage.service';
import { PageDataPanelComponent } from '../../shared/page-layout/page-data-panel.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageMetricStripComponent } from '../../shared/page-layout/page-metric-strip.component';
import { PageSearchPanelComponent } from '../../shared/page-layout/page-search-panel.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { PageMetric } from '../../shared/page-layout/page-layout.models';
import { Products } from '../models/product.model';
import { OrderListRow, OrderSavedView, OrderSearchRequest, OrderSummary } from '../order-workbench.model';
import { OrdersService } from '../order.service';
import { ProductsService } from '../product.service';
import { PurchaseOrder } from './purchase-order.model';
import { getOrderStatusMetadata, isConfirmedOrderStatus, isDraftOrderStatus } from '../order-status-metadata';
import { LogisticsProjectsService } from 'src/app/logistics-projects/logistics-projects.service';
import { LogisticsProjectLookupComponent } from 'src/app/logistics-projects/logistics-project-lookup.component';
import { LogisticsProjectSummary } from 'src/app/logistics-projects/models/logistics-project.model';

interface WorkbenchPresetView {
    key: string;
    label: string;
    filters: Partial<OrderSearchRequest>;
}

@Component({
    selector: 'app-purchase-order',
    templateUrl: './purchase-order.component.html',
    styleUrls: ['./purchase-order.component.css'],
    standalone: true,
    imports: [
        LogisticsProjectLookupComponent,
        CommonModule,
        FormsModule,
        RouterModule,
        ToastModule,
        ButtonModule,
        TableModule,
        ToolbarModule,
        IftaLabelModule,
        InputTextModule,
        InputNumberModule,
        InputGroupModule,
        InputGroupAddonModule,
        DatePickerModule,
        SelectModule,
        MultiSelectModule,
        ProgressBarModule,
        BadgeModule,
        TooltipModule,
        PageShellComponent,
        PageHeroComponent,
        PageMetricStripComponent,
        PageSearchPanelComponent,
        PageDataPanelComponent
    ],
    providers: [
        MessageService,
        OrdersService
    ]
})
export class PurchaseOrderComponent implements OnInit {
    breadcrumbItems: MenuItem[] | undefined;
    homeBreadcrumb: MenuItem | undefined;

    purchaseOrders: OrderListRow[] = [];
    selectedPurchaseOrders: OrderListRow[] = [];
    suppliers: Customer[] = [];
    products: Products[] = [];
    logisticsProjects: LogisticsProjectSummary[] = [];
    private projectNamesRequest = 0;
    savedViews: OrderSavedView[] = [];
    activeSavedViewId: number | undefined;
    activePresetView = 'ALL';
    savedViewName = '';
    filterModel: OrderSearchRequest = {};
    summary: OrderSummary | undefined;
    isLoading = false;
    lastLazyEvent: any;
    first = 0;
    rows = 20;
    totalRecords = 0;
    salespersonList: SelectItem<any>[] | undefined;

    statusOptions = [
        { label: 'Draft', value: 'DRAFT' },
        { label: 'Pending approval', value: 'PENDING_APPROVAL' },
        { label: 'Approved', value: 'APPROVED' },
        { label: 'Confirmed', value: 'CONFIRMED' },
        { label: 'On hold', value: 'ON_HOLD' },
        { label: 'Released', value: 'RELEASED' },
        { label: 'Partially received', value: 'PARTIALLY_RECEIVED' },
        { label: 'Received', value: 'RECEIVED' },
        { label: 'Partially billed', value: 'PARTIALLY_BILLED' },
        { label: 'Billed', value: 'BILLED' },
        { label: 'Invoiced', value: 'INVOICED' },
        { label: 'Cancelled', value: 'CANCELLED' },
        { label: 'Closed', value: 'CLOSED' },
        { label: 'Revised', value: 'REVISED' }
    ];

    currencyOptions = [
        { label: 'SAR', value: 'SAR' },
        { label: 'USD', value: 'USD' },
        { label: 'AED', value: 'AED' },
        { label: 'EUR', value: 'EUR' }
    ];

    approvalStatusOptions = [
        { label: 'No approval required', value: 'NOT_REQUIRED' },
        { label: 'Pending approval', value: 'PENDING_APPROVAL' },
        { label: 'Approved', value: 'APPROVED' },
        { label: 'Rejected', value: 'REJECTED' }
    ];

    receivingStatusOptions = [
        { label: 'Not received', value: 'NOT_RECEIVED' },
        { label: 'Partially received', value: 'PARTIALLY_RECEIVED' },
        { label: 'Received', value: 'RECEIVED' }
    ];

    billingStatusOptions = [
        { label: 'Not billed', value: 'NOT_BILLED' },
        { label: 'Ready to bill', value: 'READY_TO_BILL' },
        { label: 'Partially billed', value: 'PARTIALLY_BILLED' },
        { label: 'Billed', value: 'BILLED' }
    ];

    matchingStatusOptions = [
        { label: 'Not started', value: 'NOT_STARTED' },
        { label: 'Matching', value: 'MATCHING' },
        { label: 'Matched', value: 'MATCHED' },
        { label: 'Match exception', value: 'MATCH_EXCEPTION' }
    ];

    holdStatusOptions = [
        { label: 'Clear', value: 'CLEAR' },
        { label: 'On hold', value: 'ON_HOLD' },
        { label: 'Blocked', value: 'BLOCKED' }
    ];

    presetViews: WorkbenchPresetView[] = [
        { key: 'DRAFT', label: 'Draft', filters: { statuses: ['DRAFT'] } },
        { key: 'AWAITING_APPROVAL', label: 'Awaiting approval', filters: { approvalStatuses: ['PENDING_APPROVAL'] } },
        { key: 'APPROVED', label: 'Approved', filters: { approvalStatuses: ['APPROVED'] } },
        { key: 'SENT_TO_SUPPLIER', label: 'Sent to supplier', filters: { statuses: ['RELEASED'] } },
        { key: 'AWAITING_RECEIPT', label: 'Awaiting receipt', filters: { receivingStatuses: ['NOT_RECEIVED'] } },
        { key: 'PARTIALLY_RECEIVED', label: 'Partially received', filters: { receivingStatuses: ['PARTIALLY_RECEIVED'] } },
        { key: 'READY_FOR_BILL', label: 'Ready for bill', filters: { receivingStatuses: ['RECEIVED'], billingStatuses: ['NOT_BILLED'] } },
        { key: 'MATCH_EXCEPTIONS', label: 'Match exceptions', filters: { matchingStatuses: ['MATCH_EXCEPTION'] } },
        { key: 'CLOSED', label: 'Closed', filters: { statuses: ['CLOSED'] } }
    ];

    constructor(
        private ordersService: OrdersService,
        private messageService: MessageService,
        private router: Router,
        private route: ActivatedRoute,
        private sessionStorageService: SessionStorageService,
        private customerService: CustomerService,
        private productsService: ProductsService,
        private logisticsProjectsService: LogisticsProjectsService
    ) {
    }

    ngOnInit(): void {
        this.breadcrumbItems = [{ label: 'Purchase Order' }];
        this.homeBreadcrumb = { icon: 'pi pi-home', routerLink: '/' };
        this.resetFilters();
        const projectId = Number(this.route.snapshot.queryParamMap.get('projectId') || '');
        if (projectId) {
            this.filterModel.logisticsProjectId = projectId;
        }
        this.loadFilterData();
        this.loadSavedViews();
        this.loadData();
    }

    loadData(event?: any): void {
        this.isLoading = true;
        this.lastLazyEvent = event ?? this.lastLazyEvent ?? { first: this.first, rows: this.rows };
        this.first = this.lastLazyEvent.first ?? 0;
        this.rows = this.lastLazyEvent.rows ?? 20;

        const request = this.buildRequest();
        request.page = Math.floor(this.first / this.rows);
        request.pageSize = this.rows || 20;
        request.sortField = this.normalizeSortField(this.lastLazyEvent.sortField);
        request.sortDirection = this.lastLazyEvent.sortOrder === 1 ? 'asc' : 'desc';

        forkJoin({
            list: this.ordersService.searchPurchaseOrders(request),
            summary: this.ordersService.getPurchaseOrderSummary({ ...request, page: 0, pageSize: 100000 })
        }).subscribe({
            next: ({ list, summary }) => {
                this.purchaseOrders = list.items ?? [];
                this.loadPageProjectNames(this.purchaseOrders);
                this.totalRecords = list.count ?? 0;
                this.summary = summary;
                this.selectedPurchaseOrders = [];
                this.isLoading = false;
            },
            error: () => {
                this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Error loading purchase orders' });
                this.isLoading = false;
            }
        });
    }

    addPurchaseOrder(): void {
        this.router.navigate(['/order/purchase-order/add'], {
            queryParams: this.filterModel.logisticsProjectId ? { projectId: this.filterModel.logisticsProjectId } : undefined
        });
    }

    editPurchaseOrder(purchaseOrder: OrderListRow): void {
        this.router.navigate(['/order/purchase-order/edit', purchaseOrder.id]);
    }

    viewPurchaseOrder(purchaseOrder: OrderListRow): void {
        this.router.navigate(['/order/purchase-order/view', purchaseOrder.id]);
    }

    applySearch(): void {
        this.first = 0;
        this.loadData({ first: 0, rows: this.rows, sortField: this.lastLazyEvent?.sortField, sortOrder: this.lastLazyEvent?.sortOrder });
    }

    clearSearch(): void {
        this.resetFilters();
        this.activeSavedViewId = undefined;
        this.activePresetView = 'ALL';
        this.applySearch();
    }

    saveCurrentView(): void {
        const name = this.savedViewName.trim() || `Purchase view ${this.savedViews.length + 1}`;
        this.ordersService.createOrderSavedView({
            module: 'PURCHASE_ORDER',
            name,
            userId: this.userId(),
            owningCompanyId: this.companyId(),
            filtersJson: JSON.stringify(this.buildRequest(false)),
            sortJson: JSON.stringify({
                sortField: this.lastLazyEvent?.sortField ?? 'createdAt',
                sortDirection: this.lastLazyEvent?.sortOrder === 1 ? 'asc' : 'desc'
            }),
            defaultView: this.savedViews.length === 0
        }).subscribe({
            next: (savedView) => {
                this.savedViewName = '';
                this.activeSavedViewId = savedView.savedViewId;
                this.loadSavedViews();
                this.messageService.add({ severity: 'success', summary: 'Saved', detail: 'Saved view created' });
            },
            error: () => this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Unable to save view' })
        });
    }

    applySavedView(savedView: OrderSavedView): void {
        this.activeSavedViewId = savedView.savedViewId;
        this.activePresetView = '';
        try {
            this.filterModel = {
                ...this.emptyRequest(),
                ...JSON.parse(savedView.filtersJson || '{}'),
                owningCompanyId: this.companyId()
            };
            const sort = JSON.parse(savedView.sortJson || '{}');
            this.lastLazyEvent = {
                first: 0,
                rows: this.rows,
                sortField: sort.sortField,
                sortOrder: sort.sortDirection === 'asc' ? 1 : -1
            };
            this.applySearch();
        } catch {
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Saved view filters are invalid' });
        }
    }

    applyPresetView(view: WorkbenchPresetView): void {
        this.filterModel = {
            ...this.emptyRequest(),
            ...view.filters
        };
        this.activePresetView = view.key;
        this.activeSavedViewId = undefined;
        this.applySearch();
    }

    exportOrders(selectedOnly = false): void {
        const request = this.buildRequest(false);
        if (selectedOnly) {
            request.selectedIds = this.selectedPurchaseOrders.map(order => order.id);
        }
        this.ordersService.exportPurchaseOrders(request, 'xlsx').subscribe({
            next: (blob) => this.download(blob, 'purchase-orders.xlsx'),
            error: () => this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Unable to export purchase orders' })
        });
    }

    canConfirm(purchaseOrder: OrderListRow | undefined): boolean {
        if (!purchaseOrder) return false;
        if (this.isConfirmed(purchaseOrder.state)) {
            return false;
        }
        const state = (purchaseOrder.state || '').toUpperCase();
        if (state === 'CANCELLED' || state === 'CLOSED' || state === 'REJECTED') {
            return false;
        }
        return state === 'APPROVED' || state === 'DRAFT' || (purchaseOrder.approvalStatus || '').toUpperCase() === 'APPROVED';
    }

    confirmPurchaseOrder(purchaseOrder: OrderListRow): void {
        if (!this.canConfirm(purchaseOrder)) {
            return;
        }
        this.isLoading = true;
        this.ordersService.confirmPurchaseOrder({
            purchaseOrderID: purchaseOrder.id,
            purchaseOrderNumber: purchaseOrder.orderNumber,
            state: purchaseOrder.state
        } as PurchaseOrder).subscribe({
            next: () => {
                this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Order# ' + purchaseOrder.orderNumber + ' confirmed successfully' });
                this.loadData();
            },
            error: () => {
                this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Unable to confirm purchase order' });
                this.isLoading = false;
            }
        });
    }

    get metrics(): PageMetric[] {
        const draft = this.statusMetric('DRAFT');
        const awaitingApproval = this.statusMetric('PENDING_APPROVAL', 'approvalStatuses');
        const approved = this.statusMetric('APPROVED', 'approvalStatuses');
        const closed = this.statusMetric('CLOSED');
        return [
            { label: 'Draft amount', value: this.formatAmount(draft.totalAmount), hint: draft.count, tone: 'warn' },
            { label: 'Awaiting approval', value: this.formatAmount(awaitingApproval.totalAmount), hint: awaitingApproval.count, tone: 'warn' },
            { label: 'Approved amount', value: this.formatAmount(approved.totalAmount), hint: approved.count, tone: 'success' },
            { label: 'Overdue expected', value: this.summary?.overdueExpectedCount ?? 0, tone: 'danger' },
            { label: 'Awaiting receipt', value: this.formatAmount(this.statusAmount(['NOT_RECEIVED'], 'receivingStatuses')), tone: 'info' },
            { label: 'Match exceptions', value: this.formatAmount(this.statusAmount(['MATCH_EXCEPTION'], 'matchingStatuses')), hint: this.statusCount(['MATCH_EXCEPTION'], 'matchingStatuses'), tone: 'danger' },
            { label: 'Closed amount', value: this.formatAmount(closed.totalAmount), hint: closed.count, tone: 'default' }
        ];
    }

    get activeFilterCount(): number {
        const filter = this.filterModel;
        let count = 0;
        if (filter.freeText) count++;
        if (filter.partyId) count++;
        if (filter.orderNumber) count++;
        if (filter.statuses?.length) count++;
        if (filter.approvalStatuses?.length) count++;
        if (filter.receivingStatuses?.length) count++;
        if (filter.billingStatuses?.length) count++;
        if (filter.matchingStatuses?.length) count++;
        if (filter.holdStatuses?.length) count++;
        if (filter.currencyCode) count++;
        if (filter.orderDateFrom) count++;
        if (filter.orderDateTo) count++;
        if (filter.expectedDateFrom) count++;
        if (filter.expectedDateTo) count++;
        if (filter.productId) count++;
        if (filter.logisticsProjectId) count++;
        if (filter.amountMin !== undefined && filter.amountMin !== null) count++;
        if (filter.amountMax !== undefined && filter.amountMax !== null) count++;
        return count;
    }

    getPurchaseOrderTotalAmount(purchaseOrder: OrderListRow): string {
        return purchaseOrder.totalAmount !== null && purchaseOrder.totalAmount !== undefined
            ? this.formatAmount(purchaseOrder.totalAmount, purchaseOrder.currencyCode)
            : '-';
    }

    isDraft(state: string | undefined): boolean {
        return isDraftOrderStatus(state);
    }

    isConfirmed(state: string | undefined): boolean {
        return isConfirmedOrderStatus(state);
    }

    statusLabel(state: string | undefined): string {
        return getOrderStatusMetadata(state).label;
    }

    statusSeverity(state: string | undefined): 'success' | 'secondary' | 'info' | 'warn' | 'danger' | 'contrast' {
        return getOrderStatusMetadata(state).severity;
    }

    statusTooltip(state: string | undefined): string {
        return getOrderStatusMetadata(state).tooltip;
    }

    projectLabel(projectId: number | undefined): string {
        if (!projectId) {
            return '-';
        }
        const project = this.logisticsProjects.find(item => item.projectId === projectId);
        return project ? `${project.projectNumber || 'Project'} - ${project.projectName || projectId}` : `Project #${projectId}`;
    }

    private loadFilterData(): void {
        const owningCompanyId = this.companyId();
        forkJoin({
            suppliers: this.customerService.getCustomersByCompany(owningCompanyId),
            products: this.productsService.getAllProductsByOwningCompany(owningCompanyId)
        }).subscribe({
            next: ({ suppliers, products }) => {
                this.suppliers = suppliers ?? [];
                this.products = products ?? [];
            }
        });
    }

    private loadPageProjectNames(rows: OrderListRow[]): void {
        const request = ++this.projectNamesRequest;
        this.logisticsProjects = [];
        const ids = [...new Set(rows.map(row => row.logisticsProjectId).filter((id): id is number => !!id))];
        if (!ids.length) return;
        this.logisticsProjectsService.lookupProjects(this.companyId(), '', ids).subscribe({
            next: projects => { if (request === this.projectNamesRequest) this.logisticsProjects = projects; },
            error: () => { if (request === this.projectNamesRequest) this.logisticsProjects = []; }
        });
    }

    private loadSavedViews(): void {
        this.ordersService.getOrderSavedViews('PURCHASE_ORDER', this.companyId(), this.userId()).subscribe({
            next: (views) => {
                this.savedViews = views ?? [];
                const defaultView = this.savedViews.find(view => view.defaultView);
                if (defaultView && !this.activeSavedViewId) {
                    this.applySavedView(defaultView);
                }
            }
        });
    }

    private buildRequest(includePage = true): OrderSearchRequest {
        const request: OrderSearchRequest = {
            ...this.filterModel,
            owningCompanyId: this.companyId()
        };
        if (!includePage) {
            delete request.page;
            delete request.pageSize;
        }
        return request;
    }

    private emptyRequest(): OrderSearchRequest {
        return {
            owningCompanyId: this.companyId(),
            statuses: [],
            currencyCode: undefined,
            sortField: 'createdAt',
            sortDirection: 'desc'
        };
    }

    private resetFilters(): void {
        this.filterModel = this.emptyRequest();
    }

    private statusMetric(
        status: string,
        group: 'statuses' | 'approvalStatuses' | 'receivingStatuses' | 'billingStatuses' | 'matchingStatuses' | 'holdStatuses' = 'statuses'
    ): { count: number; totalAmount: number } {
        const found = this.summary?.[group]?.find(item => item.status === status);
        return { count: found?.count ?? 0, totalAmount: found?.totalAmount ?? 0 };
    }

    private statusCount(
        statuses: string[],
        group: 'statuses' | 'approvalStatuses' | 'receivingStatuses' | 'billingStatuses' | 'matchingStatuses' | 'holdStatuses' = 'statuses'
    ): number {
        return statuses.reduce((total, status) => total + this.statusMetric(status, group).count, 0);
    }

    private statusAmount(
        statuses: string[],
        group: 'statuses' | 'approvalStatuses' | 'receivingStatuses' | 'billingStatuses' | 'matchingStatuses' | 'holdStatuses' = 'statuses'
    ): number {
        return statuses.reduce((total, status) => total + this.statusMetric(status, group).totalAmount, 0);
    }

    private formatAmount(amount: number | undefined, currency = 'SAR'): string {
        return amount !== undefined && amount !== null ? `${currency} ${Number(amount).toFixed(currencyFractionDigits(currency))}` : '-';
    }

    private normalizeSortField(sortField: string | string[] | undefined): string {
        return Array.isArray(sortField) ? sortField[0] : sortField ?? 'createdAt';
    }

    private download(blob: Blob, filename: string): void {
        const url = window.URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = filename;
        anchor.click();
        window.URL.revokeObjectURL(url);
    }

    private companyId(): number {
        return this.sessionStorageService.getCompany()?.id ?? 0;
    }

    private userId(): number {
        return this.sessionStorageService.getCustomer()?.id ?? this.sessionStorageService.getUser()?.id ?? 0;
    }
}
